import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, Check, Package, Search, Filter, DollarSign } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const VALIDITY_OPTIONS = [
  { label: '3 Days (Demo)', days: 3, is_demo: true },
  { label: '1 Month', days: 30, is_demo: false },
  { label: '3 Months', days: 90, is_demo: false },
  { label: '6 Months', days: 180, is_demo: false },
  { label: '12 Months', days: 365, is_demo: false },
];

const deletePackageSafely = async (url) => {
  try {
    return await api.delete(url);
  } catch (err) {
    const d = err.response && err.response.data;
    if (err.response && err.response.status === 409 && d && d.needs_confirm) {
      if (!window.confirm(d.message + '\n\nDelete it anyway?')) throw new Error('cancelled');
      try {
        return await api.delete(url + (String(url).includes('?') ? '&' : '?') + 'confirm=1');
      } catch (err2) {
        const d2 = err2.response && err2.response.data;
        if (d2 && d2.message) toast.error(d2.message, { duration: 9000 });
        throw err2;
      }
    }
    if (d && d.message) toast.error(d.message, { duration: 9000 });
    throw err;
  }
};

const Packages = () => {
  const { user: me } = useAuth();
  const [packages, setPackages] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editPkg, setEditPkg] = useState(null);
  const [form, setForm] = useState({
    name: '', description: '', channel_ids: [],
    validity_days: 30, validity_label: "1 Month", is_demo: false, is_adult_pack: false, is_movies_pack: false, is_vod_pack: false
  });

  // Channel filter state
  const [chSearch, setChSearch] = useState('');
  const [filterGenre, setFilterGenre] = useState('all');
  const [filterLang, setFilterLang] = useState('all');
  const [showPricing, setShowPricing] = useState(false);
  const [pricingPkg, setPricingPkg] = useState(null);
  const [pricingForm, setPricingForm] = useState({ wholesale_price: '', retail_price: '', price_model: 'perpack' });
  const [savingPrice, setSavingPrice] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [pkgs, chs] = await Promise.all([api.get('/api/packages'), api.get('/api/channels')]);
      setPackages(pkgs.data.data || []);
      setChannels(chs.data.data || []);
    } catch (err) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editPkg) {
        await api.put(`/api/packages/${editPkg.id}`, form);
        await api.put(`/api/packages/${editPkg.id}/channels`, { channel_ids: form.channel_ids });
        toast.success('Package updated');
      } else {
        await api.post('/api/packages', form);
        toast.success('Package created');
      }
      setShowModal(false);
      setEditPkg(null);
      resetForm();
      fetchAll();
    } catch (err) { toast.error('Failed to save'); }
  };

  const resetForm = () => setForm({
    name: '', description: '', channel_ids: [], is_active: true,
    validity_days: 30, validity_label: "1 Month", is_demo: false, is_adult_pack: false, is_movies_pack: false, is_vod_pack: false,
    billing_model: 'per_activation', wholesale_price: 0
  });

  const handleEdit = async (pkg) => {
    const res = await api.get(`/api/packages/${pkg.id}/channels`);
    const ids = res.data.data?.map(c => c.id) || [];
    setEditPkg(pkg);
    setForm({
      name: pkg.name,
      description: pkg.description || '',
      channel_ids: ids,
      is_active: pkg.is_active !== undefined ? pkg.is_active : true,
      validity_days: pkg.validity_days || 30,
      validity_label: pkg.validity_label || '1 Month',
      is_demo: pkg.is_demo || false,
      is_adult_pack: pkg.is_adult_pack || false,
      is_movies_pack: pkg.is_movies_pack || false,
      is_vod_pack: pkg.is_vod_pack || false,
      billing_model: pkg.billing_model || 'per_activation',
      wholesale_price: pkg.wholesale_price || 0
    });
    setChSearch('');
    setFilterGenre('all');
    setFilterLang('all');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this package?')) return;
    try { await deletePackageSafely(`/api/packages/${id}`); toast.success('Deleted'); fetchAll(); }
    catch (err) { toast.error('Failed to delete'); }
  };

  const toggleChannel = (id) => {
    setForm(f => ({
      ...f,
      channel_ids: f.channel_ids.includes(id)
        ? f.channel_ids.filter(c => c !== id)
        : [...f.channel_ids, id]
    }));
  };

  const selectAll = () => {
    const ids = filteredChannels.map(c => c.id);
    setForm(f => ({ ...f, channel_ids: [...new Set([...f.channel_ids, ...ids])] }));
  };

  const clearAll = () => {
    const ids = filteredChannels.map(c => c.id);
    setForm(f => ({ ...f, channel_ids: f.channel_ids.filter(id => !ids.includes(id)) }));
  };

  const handleValidityChange = (option) => {
    setForm(f => ({ ...f, validity_days: option.days, validity_label: option.label, is_demo: option.is_demo }));
  };

  // Get unique genres and languages
  const genres = ['all', 'Adult (18+)', 'Movies Pack', 'VOD Pack', ...new Set(channels.map(c => c.genre).filter(Boolean).sort())];
  const languages = ['all', ...new Set(channels.map(c => c.language).filter(Boolean).sort())];

  const filteredChannels = channels.filter(c => {
    const matchSearch = !chSearch || c.name?.toLowerCase().includes(chSearch.toLowerCase());
    const matchGenre = filterGenre === 'all' ||
      (filterGenre === 'Adult (18+)' ? c.is_adult === true :
      filterGenre === 'Movies Pack' ? c.genre === 'Movies' :
      filterGenre === 'VOD Pack' ? c.genre === 'VOD' :
      c.genre === filterGenre);
    const matchLang = filterLang === 'all' || c.language === filterLang;
    return matchSearch && matchGenre && matchLang;
  });

  const validityColor = { '3 Days (Demo)': '#f7a24f', '1 Month': '#4f6ef7', '3 Months': '#00d4aa', '6 Months': '#a78bfa', '12 Months': '#f7d44f' };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Packages</div>
          <div className="page-subtitle">{packages.length} packages</div>
        </div>
        {me?.role === 'superadmin' && (
          <button className="btn btn-primary" onClick={() => { setShowModal(true); setEditPkg(null); resetForm(); setChSearch(''); setFilterGenre('all'); setFilterLang('all'); }}>
            <Plus size={16} /> Create Package
          </button>
        )}
      </div>

      {loading ? <div className="loading"><div className="spinner" />Loading...</div> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
          {packages.map(pkg => (
            <div key={pkg.id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div style={{ background: 'rgba(79,110,247,0.15)', borderRadius: '10px', padding: '10px' }}>
                  <Package size={20} color="var(--accent-blue)" />
                </div>
                {me?.role === 'superadmin' && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => { setPricingPkg(pkg); setPricingForm({ wholesale_price: '', retail_price: '', price_model: 'perpack' }); setShowPricing(true); }} className="btn btn-secondary" style={{ padding: '6px 10px', color: '#00B4D8' }} title="Set Pricing"><DollarSign size={14} /></button>
                    <button onClick={() => handleEdit(pkg)} className="btn btn-secondary" style={{ padding: '6px 10px' }}><Edit2 size={14} /></button>
                    <button onClick={() => handleDelete(pkg.id)} className="btn btn-danger" style={{ padding: '6px 10px' }}><Trash2 size={14} /></button>
                  </div>
                )}
              </div>
              <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '4px' }}>{pkg.name}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>{pkg.description || 'No description'}</div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <span className="badge badge-blue">{pkg.channel_count} channels</span>
                <span className="badge" style={{
                  background: `${validityColor[pkg.validity_label] || '#4f6ef7'}20`,
                  color: validityColor[pkg.validity_label] || '#4f6ef7'
                }}>
                  {pkg.is_adult_pack ? "🔞 " : ""}{pkg.is_movies_pack ? "🎬 " : ""}{pkg.is_vod_pack ? "📹 " : ""}{pkg.is_demo ? "🎯 " : ""}{pkg.validity_label || "1 Month"}
                </span>
                <span className={`badge ${pkg.is_active ? 'badge-success' : 'badge-danger'}`}>
                  {pkg.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" style={{ maxWidth: '700px', maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{editPkg ? 'Edit Package' : 'Create Package'}</div>
              <button className="close-btn" onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              {/* Basic Info */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                <div className="form-group">
                  <label>Package Name *</label>
                  <input className="form-control" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required placeholder="e.g. Basic Pack" />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input className="form-control" value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Optional" />
                </div>
              </div>

              {/* Validity */}
              <div className="form-group">
                <label>Validity / Duration</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {VALIDITY_OPTIONS.map(opt => (
                    <button key={opt.label} type="button" onClick={() => handleValidityChange(opt)}
                      className="btn" style={{
                        padding: '8px 16px', fontSize: '13px',
                        background: form.validity_label === opt.label
                          ? (opt.is_demo ? 'var(--accent-orange)' : 'var(--accent-blue)')
                          : 'var(--bg-primary)',
                        color: form.validity_label === opt.label ? 'white' : 'var(--text-secondary)',
                        border: `1px solid ${form.validity_label === opt.label ? 'transparent' : 'var(--border)'}`,
                      }}>
                      {opt.is_demo ? '🎯 ' : ''}{opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Pack Type Indicator */}
              {(form.is_adult_pack || form.is_movies_pack) && (
                <div className="form-group">
                  <div style={{
                    display: "flex", alignItems: "center", gap: "10px",
                    background: form.is_adult_pack ? "rgba(247,95,95,0.08)" : "rgba(79,110,247,0.08)",
                    border: form.is_adult_pack ? "1px solid rgba(247,95,95,0.3)" : "1px solid rgba(79,110,247,0.3)",
                    borderRadius: "8px", padding: "12px 16px"
                  }}>
                    <span style={{ fontSize: "20px" }}>{form.is_adult_pack ? "🔞" : "🎬"}</span>
                    <div>
                      <div style={{ fontWeight: "600", fontSize: "14px" }}>
                        {form.is_adult_pack ? "Adult Pack (18+)" : "Movies Pack"}
                      </div>
                      <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>
                        {form.is_adult_pack ? "This pack will require adult PIN to access" : "This pack contains movie channels only"}
                      </div>
                    </div>
                    <button type="button" onClick={() => { setForm(f => ({...f, is_adult_pack: false, is_movies_pack: false})); setFilterGenre('all'); }}
                      style={{ marginLeft: "auto", background: "none", border: "none", color: "var(--text-secondary)", cursor: "pointer", fontSize: "12px" }}>
                      ✕ Clear
                    </button>
                  </div>
                </div>
              )}
              <div className="form-group">
                <label>Billing Model *</label>
                <select className="form-control" value={form.billing_model} onChange={e => setForm({...form, billing_model: e.target.value})}>
                  <option value="per_activation">Per Activation - charge wallet each time assigned</option>
                  <option value="tenant_included">Tenant Included - covered by flat monthly platform fee</option>
                  <option value="demo_only">Demo / Trial - never charges wallet</option>
                </select>
              </div>
              {form.billing_model === 'per_activation' && (
                <div className="form-group">
                  <label>Wholesale Price (₹ per activation) *</label>
                  <input className="form-control" type="number" min="0" step="0.01" value={form.wholesale_price} onChange={e => setForm({...form, wholesale_price: e.target.value})} placeholder="e.g. 100.00" />
                </div>
              )}

              {/* Channel Filters */}
              <div style={{ marginBottom: '8px' }}>
                <label style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: '500', display: 'block', marginBottom: '8px' }}>
                  Select Channels <span style={{ color: 'var(--accent-blue)' }}>({form.channel_ids.length} selected)</span>
                </label>

                {/* Search and filters */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '150px', display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: '8px', padding: '6px 12px' }}>
                    <Search size={14} color="var(--text-secondary)" />
                    <input value={chSearch} onChange={e => setChSearch(e.target.value)} placeholder="Search channels..." style={{ background: 'none', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: '13px', width: '100%' }} />
                  </div>
                  <select className="form-control" style={{ width: 'auto', fontSize: '13px' }} value={filterGenre} onChange={e => { const v = e.target.value; setFilterGenre(v); setForm(f => ({...f, is_adult_pack: v === 'Adult (18+)', is_movies_pack: v === 'Movies Pack', is_vod_pack: v === 'VOD Pack'})); }}>
                    {genres.map(g => <option key={g} value={g}>{g === 'all' ? 'All Genres' : g}</option>)}
                  </select>
                  <select className="form-control" style={{ width: 'auto', fontSize: '13px' }} value={filterLang} onChange={e => setFilterLang(e.target.value)}>
                    {languages.map(l => <option key={l} value={l}>{l === 'all' ? 'All Languages' : l}</option>)}
                  </select>
                </div>

                {form.is_vod_pack && (
                  <div style={{ padding: '16px', background: 'rgba(0,180,216,0.08)', border: '1px solid rgba(0,180,216,0.2)', borderRadius: '8px', marginBottom: '12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    📹 <strong style={{ color: 'var(--text-primary)' }}>VOD Pack</strong> — Subscribers with this pack will have access to all movies added under Movies/VOD section. No channel selection needed.
                  </div>
                )}
                {/* Select/Clear all filtered */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <button type="button" onClick={selectAll} className="btn btn-secondary" style={{ padding: '4px 12px', fontSize: '12px' }}>
                    Select All ({filteredChannels.length})
                  </button>
                  <button type="button" onClick={clearAll} className="btn btn-secondary" style={{ padding: '4px 12px', fontSize: '12px' }}>
                    Clear Filtered
                  </button>
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)', alignSelf: 'center' }}>
                    Showing {filteredChannels.length} of {channels.length}
                  </span>
                </div>

                {/* Channel list */}
                <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '8px', padding: '6px' }}>
                  {filteredChannels.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)', fontSize: '13px' }}>No channels match filters</div>
                  ) : (
                    filteredChannels.map(ch => (
                      <div key={ch.id} onClick={() => toggleChannel(ch.id)} style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '6px 8px', borderRadius: '6px', cursor: 'pointer',
                        background: form.channel_ids.includes(ch.id) ? 'rgba(79,110,247,0.1)' : 'transparent',
                        marginBottom: '2px'
                      }}>
                        <div style={{
                          width: '16px', height: '16px', borderRadius: '4px', flexShrink: 0,
                          border: `2px solid ${form.channel_ids.includes(ch.id) ? 'var(--accent-blue)' : 'var(--border)'}`,
                          background: form.channel_ids.includes(ch.id) ? 'var(--accent-blue)' : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                          {form.channel_ids.includes(ch.id) && <Check size={10} color="white" />}
                        </div>
                        <span style={{ fontSize: '13px', flex: 1 }}>{ch.lcn ? `[${ch.lcn}] ` : ''}{ch.name}</span>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{ch.language}</span>
                        <span className="badge badge-blue" style={{ fontSize: '10px', padding: '2px 6px' }}>{ch.genre}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '12px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary"><Check size={16} />{editPkg ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Pricing Modal */}
      {showPricing && pricingPkg && (
        <div className="modal-overlay" onClick={() => setShowPricing(false)}>
          <div className="modal" style={{ maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">💰 Set Pricing — {pricingPkg.name}</div>
              <button className="close-btn" onClick={() => setShowPricing(false)}><X size={20} /></button>
            </div>
            <div className="modal-body" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ background: 'rgba(0,180,216,0.08)', border: '1px solid rgba(0,180,216,0.2)', borderRadius: '8px', padding: '12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                <strong style={{ color: 'var(--text-primary)' }}>{pricingPkg.validity_label || '1 Month'}</strong> · {pricingPkg.is_adult_pack ? '🔞 Adult Pack' : pricingPkg.is_movies_pack ? '🎬 Movies Pack' : '📺 Standard Pack'}
              </div>
              <div>
                <label style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px', display: 'block' }}>Price Model</label>
                <select className="form-control" value={pricingForm.price_model} onChange={e => setPricingForm(f => ({...f, price_model: e.target.value}))}>
                  <option value="perpack">Per Pack</option>
                  <option value="flat">Flat Rate</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px', display: 'block' }}>Wholesale Price (₹) <span style={{fontSize:'11px'}}>— what distributor pays</span></label>
                <input className="form-control" type="number" min="0" step="0.01" value={pricingForm.wholesale_price} onChange={e => setPricingForm(f => ({...f, wholesale_price: e.target.value}))} placeholder="e.g. 150.00" />
              </div>
              <div>
                <label style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px', display: 'block' }}>Retail Price (₹) <span style={{fontSize:'11px'}}>— what customer pays</span></label>
                <input className="form-control" type="number" min="0" step="0.01" value={pricingForm.retail_price} onChange={e => setPricingForm(f => ({...f, retail_price: e.target.value}))} placeholder="e.g. 199.00" />
              </div>
              {pricingForm.wholesale_price && pricingForm.retail_price && (
                <div style={{ background: 'rgba(0,212,170,0.08)', border: '1px solid rgba(0,212,170,0.2)', borderRadius: '8px', padding: '12px', fontSize: '13px' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Margin: </span>
                  <strong style={{ color: '#00d4aa' }}>₹{(parseFloat(pricingForm.retail_price) - parseFloat(pricingForm.wholesale_price)).toFixed(2)}</strong>
                  <span style={{ color: 'var(--text-secondary)', marginLeft: '8px' }}>({(((parseFloat(pricingForm.retail_price) - parseFloat(pricingForm.wholesale_price)) / parseFloat(pricingForm.retail_price)) * 100).toFixed(1)}%)</span>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowPricing(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={savingPrice} onClick={async () => {
                if (!pricingForm.wholesale_price || !pricingForm.retail_price) { toast.error('Enter both prices'); return; }
                setSavingPrice(true);
                try {
                  await api.post('/api/packages/' + pricingPkg.id + '/pricing', pricingForm);
                  toast.success('Pricing saved!');
                  setShowPricing(false);
                } catch { toast.error('Failed to save pricing'); }
                setSavingPrice(false);
              }}>
                {savingPrice ? 'Saving...' : '💾 Save Pricing'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Packages;
